import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Coins, Check, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useProgress } from "../context/ProgressContext";
import { walletOf, buyAvatar } from "../lib/coins";
import { Avatar, AVATAR_BGS } from "../components/Avatar";

const TIER_STYLE = {
  0: "text-muted-foreground border-border",
  1: "text-phosphor border-phosphor/50",
  2: "text-sky-300 border-sky-400/50",
  3: "text-violet-300 border-violet-400/50",
  4: "text-amber-300 border-amber-400/50",
  5: "text-rose-300 border-rose-400/60",
};

export default function Avatars() {
  const { progress, update, isGuest } = useProgress();
  const [catalog, setCatalog] = useState([]);
  const wallet = useMemo(() => walletOf(progress?.stats), [progress?.stats]);

  useEffect(() => { api.get("/avatars").then((r) => setCatalog(r.data)); }, []);

  const equipped = catalog.find((a) => a.id === wallet.avatar);

  const buy = (a) => {
    const res = buyAvatar(progress?.stats, a);
    if (!res.ok) return toast.error(res.reason);
    update({ stats: res.stats });
    toast.success(`${a.name} joins your collection`, { description: "Equipped automatically" });
  };

  const setBg = (bgId) => {
    update({ stats: { ...(progress?.stats || {}), avatar_bg: bgId } });
  };

  const equip = (a) => {
    update({ stats: { ...(progress?.stats || {}), avatar: a.id } });
    toast.success(`${a.name} equipped`);
  };

  const tiers = [...new Set(catalog.map((a) => a.tier))].sort();

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-xs tracking-widest text-muted-foreground hover:text-foreground mb-6" data-testid="back-dashboard">
        <ArrowLeft className="w-3 h-3" /> BACK
      </Link>

      <div className="panel p-6 mb-8 flex flex-col md:flex-row md:items-center gap-6" data-testid="avatar-wallet">
        <Avatar id={wallet.avatar} bg={wallet.bg} size={112} ring testid="equipped-avatar" />
        <div className="flex-1">
          <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-1">// AVATAR SHOP</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Equipped: <span className="text-phosphor" data-testid="equipped-name">{equipped?.name || "Baby Barnes"}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Every exercise pays out <span className="text-foreground">1 coin per 100 points</span> (minimum 1 per completed run). Spend coins to recruit new characters; equip any you own.
            {isGuest && <span className="text-amber-300"> Guest coins live on this device until you sign in.</span>}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-widest text-muted-foreground">BALANCE</div>
          <div className="text-4xl font-extrabold text-amber-300 tabular-nums inline-flex items-center gap-2" data-testid="coin-balance">
            <Coins className="w-7 h-7" /> {wallet.coins}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1" data-testid="coins-earned">{wallet.earned} earned all-time · {wallet.owned.length}/{catalog.length || "…"} collected</div>
        </div>
      </div>

      <section className="panel p-5 mb-10" data-testid="bg-picker">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-lg font-bold">Backdrop</h2>
          <span className="text-[10px] tracking-widest text-muted-foreground">FREE · APPLIES TO YOUR EQUIPPED AVATAR</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {AVATAR_BGS.map((b) => (
            <button key={b.id} onClick={() => setBg(b.id)} data-testid={`bg-${b.id}`} title={b.label}
              className={`flex flex-col items-center gap-1 p-1.5 border ${wallet.bg === b.id ? "border-phosphor" : "border-border hover:border-phosphor/50"}`}>
              <span style={{ background: b.css }} className={`block w-12 h-12 ${b.id === "none" ? "border border-dashed border-muted-foreground/50" : ""}`} />
              <span className="text-[9px] tracking-widest text-muted-foreground">{b.label.toUpperCase()}</span>
            </button>
          ))}
        </div>
      </section>

      {tiers.map((tier) => {
        const items = catalog.filter((a) => a.tier === tier);
        return (
          <section key={tier} className="mb-10" data-testid={`tier-${tier}`}>
            <div className="flex items-baseline gap-3 mb-4">
              <h2 className={`text-lg font-bold ${TIER_STYLE[tier].split(" ")[0]}`}>{items[0].tier_name}</h2>
              <span className="text-[10px] tracking-widest text-muted-foreground">{items[0].price === 0 ? "FREE" : `${items[0].price} COINS`}</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((a) => {
                const owned = wallet.owned.includes(a.id);
                const isEquipped = wallet.avatar === a.id;
                const affordable = wallet.coins >= a.price;
                return (
                  <div key={a.id} data-testid={`avatar-card-${a.id}`} data-owned={owned} className={`panel p-4 flex gap-4 border ${isEquipped ? "border-phosphor" : TIER_STYLE[tier].split(" ")[1]}`}>
                    <div className="relative shrink-0">
                      <Avatar id={a.id} bg={owned ? wallet.bg : "galaxy"} size={96} className={!a.available ? "grayscale opacity-40" : !owned ? "saturate-50" : ""} testid={`avatar-img-${a.id}`} />
                      {!a.available && <Lock className="w-6 h-6 text-muted-foreground absolute inset-0 m-auto" />}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="font-bold truncate">{a.name}</div>
                      <p className="text-[11px] text-muted-foreground leading-snug mt-1 flex-1">{a.blurb}</p>
                      <div className="mt-3">
                        {!a.available ? (
                          <span className="text-[10px] tracking-widest text-muted-foreground inline-flex items-center gap-1" data-testid={`soon-${a.id}`}><Sparkles className="w-3 h-3" /> COMING SOON</span>
                        ) : isEquipped ? (
                          <span className="text-[10px] tracking-widest text-phosphor inline-flex items-center gap-1" data-testid={`equipped-${a.id}`}><Check className="w-3 h-3" /> EQUIPPED</span>
                        ) : owned ? (
                          <button onClick={() => equip(a)} data-testid={`equip-${a.id}`} className="border border-phosphor/50 px-4 py-1.5 text-[10px] tracking-widest hover:bg-primary/10">EQUIP</button>
                        ) : (
                          <button onClick={() => buy(a)} disabled={!affordable} data-testid={`buy-${a.id}`}
                            className={`px-4 py-1.5 text-[10px] tracking-widest font-semibold inline-flex items-center gap-2 ${affordable ? "bg-amber-400 text-black hover:bg-amber-300" : "border border-border text-muted-foreground cursor-not-allowed"}`}>
                            <Coins className="w-3 h-3" /> {affordable ? `RECRUIT · ${a.price}` : `${a.price - wallet.coins} MORE`}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
